<template>
  <v-card variant="outlined" class="pa-4">
    <div
      class="d-flex align-center justify-space-between mb-3"
      style="gap: 12px"
    >
      <div>
        <div class="text-subtitle-1 font-weight-bold">{{ label }}</div>
        <div class="text-caption text-medium-emphasis">
          JPG, PNG, WEBP of GIF, maximaal {{ maxSize }}
        </div>
      </div>
      <div v-if="modelValue && !uploading" class="d-flex" style="gap: 8px">
        <v-btn
          variant="tonal"
          size="small"
          prepend-icon="mdi-image-sync"
          @click="pickFile"
        >
          Vervangen
        </v-btn>
        <v-btn
          variant="tonal"
          color="error"
          size="small"
          prepend-icon="mdi-delete"
          @click="remove"
        >
          Verwijderen
        </v-btn>
      </div>
    </div>

    <v-img
      v-if="modelValue"
      :src="modelValue"
      height="260"
      cover
      class="preview"
    >
      <template v-slot:error>
        <div class="broken">
          <v-icon size="36" class="mb-2">mdi-image-broken-variant</v-icon>
          <div class="font-weight-bold">
            Deze afbeelding kan niet geladen worden
          </div>
          <div class="text-caption">
            De link werkt niet meer. Upload een nieuwe afbeelding via
            &lsquo;Vervangen&rsquo;.
          </div>
        </div>
      </template>
    </v-img>

    <button
      v-else
      type="button"
      class="dropzone"
      :class="{ 'is-dragging': dragging }"
      :disabled="uploading"
      @click="pickFile"
      @dragenter.prevent="dragging = true"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop.prevent="onDrop"
    >
      <v-icon size="40" class="mb-2">mdi-cloud-upload-outline</v-icon>
      <div class="font-weight-bold">
        Sleep een afbeelding hierheen of klik om te kiezen
      </div>
      <div class="text-caption text-medium-emphasis">
        De afbeelding komt boven het artikel te staan.
      </div>
    </button>

    <v-progress-linear
      v-if="uploading"
      class="mt-3"
      :model-value="progress"
      height="22"
      rounded
      color="primary"
    >
      <strong style="font-size: 12px">Uploaden&hellip; {{ progress }}%</strong>
    </v-progress-linear>

    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      density="compact"
      class="mt-3"
      closable
      @click:close="error = ''"
    >
      {{ error }}
    </v-alert>

    <input
      ref="input"
      type="file"
      accept="image/*"
      class="d-none"
      @change="onSelect"
    />
  </v-card>
</template>

<script>
import {
  MAX_IMAGE_BYTES,
  formatBytes,
  uploadImage,
  validateImage,
} from "@/utils/storage";

export default {
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "Afbeelding",
    },
    folder: {
      type: String,
      default: "nieuws",
    },
  },

  // 'discarded' geeft de url door die niet meer gebruikt wordt, zodat het
  // scherm eromheen het bestand pas na een geslaagde opslag kan opruimen.
  emits: ["update:modelValue", "discarded"],

  data() {
    return {
      uploading: false,
      progress: 0,
      dragging: false,
      error: "",
      maxSize: formatBytes(MAX_IMAGE_BYTES),
    };
  },

  methods: {
    pickFile() {
      this.$refs.input.click();
    },

    onSelect(event) {
      const file = event.target.files[0];
      // Leeggooien, anders vuurt 'change' niet als hetzelfde bestand opnieuw wordt gekozen.
      event.target.value = "";
      this.upload(file);
    },

    onDrop(event) {
      this.dragging = false;
      if (!this.uploading) {
        this.upload(event.dataTransfer.files[0]);
      }
    },

    async upload(file) {
      const problem = validateImage(file);
      if (problem) {
        this.error = problem;
        return;
      }

      this.error = "";
      this.progress = 0;
      this.uploading = true;

      try {
        const url = await uploadImage(file, {
          folder: this.folder,
          onProgress: (percentage) => {
            this.progress = percentage;
          },
        });
        const previous = this.modelValue;
        this.$emit("update:modelValue", url);
        if (previous) {
          this.$emit("discarded", previous);
        }
      } catch (error) {
        console.error(error);
        this.error =
          error.code === "storage/unauthorized"
            ? "Je hebt geen rechten om te uploaden. Log opnieuw in en probeer het nog een keer."
            : "Uploaden is mislukt. Controleer je internetverbinding en probeer het nog een keer.";
      } finally {
        this.uploading = false;
      }
    },

    remove() {
      if (!confirm("Weet je zeker dat je deze afbeelding wilt verwijderen?")) {
        return;
      }
      const previous = this.modelValue;
      this.$emit("update:modelValue", "");
      this.$emit("discarded", previous);
    },
  },
};
</script>

<style lang="scss" scoped>
.preview {
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
}
.broken {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  color: rgb(var(--v-theme-error));
  background: rgba(0, 0, 0, 0.03);
}
.dropzone {
  width: 100%;
  height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  gap: 2px;
  border: 2px dashed rgba(0, 0, 0, 0.22);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.dropzone:hover,
.dropzone.is-dragging {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.06);
}
.dropzone:disabled {
  cursor: default;
  opacity: 0.6;
}
</style>
